/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";

import birdata from "../api.js";
import fieldManager from "../util/fields.js";
import util from "../util/util.js";

const program = {
	code: "bof",
	name: "Birds on Farms",
	description: `
		<p>The Birds on Farms project aims to improve populations of threatened woodland birds by supporting rural landholders to increase the extent, condition and connectivity of woodland habitat on their properties.</p>`,
	load: function() {
		return birdata.lookup().then(lookups => {
			program.options = lookups;
		});
	},
	canParticipate: function(user) {
		return user.programs && user.programs.includes("bof");
	},
	canFilterBy: function(user) {
		return this.canParticipate(user);
	},
	beforeSelect: function(survey) {
		survey.sharedSite(null);
		survey.surveyPoint(null);
	},
	afterSelect: function(survey) {
		survey.type(program.options.find('survey_type', '2_ha_search'));
		survey.durationMinutes(20);
		survey.durationHours(0);
		survey.isPrivate(true);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
		survey.sharedSite(null);
		survey.surveyPoint(null);
	},
	fields: function() {
		const coverageHelp = `
			<h3>Calculating Foliage Cover</h3>
			<p>Imagine looking at the vegetation from above (a bird's eye view) or from below (looking up at the mid-storey layer or tree canopy). Assume that each individual plant has a full cover out to the edge of its drip line. Example provided below of what the different levels of cover might look like across a 2 hectare area.
			</p>
			<ul>
			<li>
				<p>2 ha area (<5%, open ground with occasional plant)</p>
				<img src="${birdata.APP_ROOT_URL}images/lookup/bof_vegetation_coverage__sparse.png">
			</li>
			<li>
				<p>2 ha area (5–24%, more open space than plants)</p>
				<img src="${birdata.APP_ROOT_URL}images/lookup/bof_vegetation_coverage__low_to_moderate.png">
			</li>
			<li>
				<p>2 ha area (25–49%, equal open space and plants)</p>
				<img src="${birdata.APP_ROOT_URL}images/lookup/bof_vegetation_coverage__moderate.png">
			</li>
			<li>
				<p>2 ha area (50–74%, more plants than open space)</p>
				<img src="${birdata.APP_ROOT_URL}images/lookup/bof_vegetation_coverage__moderate_to_high.png">
			</li>
			<li>
				<p>2 ha area (75–100%, dense – impenetrable)</p>
				<img src="${birdata.APP_ROOT_URL}images/lookup/bof_vegetation_coverage__dense.png">
			</li>
			</ul>`;

		var fields = [
			{
				name: "siteCondition",
				minCount: 1,
				maxCount: 1,
				type: "compound",
				fields: [{
					name: "vegetationTypes",
					label: "Please describe the vegetation within the survey area (select all that apply)",
					type: "options",
					multiselect: true,
					style: "checkbox",
					defaultValue: [],
					options: program.options.bof_vegetation_type,
					required: true,
					validate: function(field) {
						let val = field.value();
						if(!val || val.length == 0) {
							return field.label + " is required";
						}
					},
					tab: "site_details"
				},
				{
					name: "otherVegetationType",
					type: "text",
					label: "",
					placeholder: "Please describe",
					hidden: function() {
						return fields.siteCondition()?.vegetationTypes()?.some(x => x.code == "other") !== true;
					},
					required: true,
					validate: "required",
					tab: "site_details"
				},
				{
					name: "treeCanopyCoverage",
					type: "options",
					label: "Extent of tree canopy cover",
					options: program.options.bof_vegetation_coverage,
					required: true,
					validate: "required",
					tab: "site_details",
					help: coverageHelp
				},
				{
					name: "shrubCoverage",
					type: "options",
					label: "Extent of shrub cover",
					options: program.options.bof_vegetation_coverage,
					required: true,
					validate: "required",
					tab: "site_details",
					help: coverageHelp
				},
				{
					name: "landUse",
					type: "options",
					label: "What is the main use of the survey plot land?",
					options: program.options.bof_land_use,
					required: true,
					validate: "required",
					tab: "site_details"
				},
				{
					name: "smallTreeCount",
					type: "options",
					label: "Approx number of small trees (trunk diameter less than 30cm)",
					options: program.options.bof_tree_count,
					required: true,
					validate: "required",
					tab: "site_details"
				},
				{
					name: "mediumTreeCount",
					type: "options",
					label: "Approx number of medium trees (trunk diameter 30-80cm)",
					options: program.options.bof_tree_count,
					required: true,
					validate: "required",
					tab: "site_details"
				},
				{
					name: "largeTreeCount",
					type: "options",
					label: "Approx number of large trees (trunk diameter more than 80cm)",
					options: program.options.bof_tree_count,
					required: true,
					validate: "required",
					tab: "site_details"
				},
				{
					name: "deadTreeCount",
					type: "options",
					label: "Approx number of dead trees still standing",
					options: program.options.bof_hollow_mistletoe_tree_count,
					required: true,
					validate: "required",
					tab: "site_details"
				},
				{
					name: "hollowBearingTreeCount",
					type: "options",
					label: "Number of trees with hollows, nest boxes, and/or hollows in arboreal termite mounds within survey area",
					options: program.options.bof_hollow_mistletoe_tree_count,
					required: true,
					validate: "required",
					tab: "site_details"
				},
				{
					name: "mistletoeTreeCount",
					type: "options",
					label: "Number of trees containing mistletoe within survey area",
					options: program.options.bof_hollow_mistletoe_tree_count,
					required: true,
					validate: "required",
					tab: "site_details"
				},
				{
					name: "logCount",
					type: "options",
					label: "Number of logs within survey area",
					options: program.options.bof_log_count,
					required: true,
					validate: "required",
					tab: "site_details",
					help: "A log is a single piece of dead timber at ground level, that is at least 1 metre long and has a diameter of 10 cm or more. One tree is considered a single log. Stumps are not included."
				},
				{
					name: "waterSources",
					label: "Which of the following occurs within the survey area (select all that apply, if any)",
					type: "options",
					options: program.options.bof_water_source,
					multiselect: true,
					style: "checkbox",
					defaultValue: [],
					tab: "site_details"
				}]
			},
			{
				name: "livestock",
				label: "What livestock currently has access to graze the plot? (select all that apply)",
				type: "options",
				options: program.options.bof_livestock,
				multiselect: true,
				style: "checkbox",
				validate: function(field) {
					let val = field.value();
					if(!val || val.length == 0) {
						return field.label + " is required";
					}
				},
				validate: "required",
				required: true,
				defaultValue: []
			},
			{
				name: "otherLivestock",
				type: "text",
				validate: "required",
				placeholder: "Please specify",
				hidden: () => !(fields.livestock.field.to_json() || []).includes("other"),
			},
			{
				name: "drinkingWaterAvailable",
				type: "boolean",
				style: "radio",
				label: "Is open/drinking water available in or within 50m of the plot? (puddles, troughs, bird baths or dams, etc.)",
				validate: "required",
				required: true,
				defaultValue: null
			},
			{
				name: "treeFloweringIntensity",
				label: "Nectar producing trees and large shrubs in flower (such as Eucalypts, Banksias, Callistemons, Kunzeas and Grevilleas)",
				type: "options",
				options: program.options.bof_flowering_intensity,
				validate: "required",
				required: true
			},
			{
				name: "mistletoeFloweringIntensity",
				label: "Mistletoe in flower",
				type: "options",
				options: program.options.bof_flowering_intensity,
				validate: "required",
				required: true
			},
			{
				name: "temperatureCelsius",
				type: "number",
				label: "Approximate temperature",
				units: "°C",
				max: 80,
				min: -30,
				decimalPlaces: 1,
				validate: "required",
				required: true
			},
			{
				name: "windSpeed",
				label: "Average wind speed",
				type: "options",
				style: "radio",
				options: program.options.bof_wind_speed,
				validate: "required",
				required: true
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, f => f.field.name);

		return fields;
	},
	programData: function(survey) {
		let fields = program.fields(survey);

		let siteDetailsAutofillState = ko.observable("none");

		function autofillSiteDetails(site) {
			siteDetailsAutofillState("loading");
			let startDate = util.formatISODate(survey.startDate());
			birdata.sharedSite(site.id, { bof_details_at: startDate }).then(function(result) {
				if(result.bof_site_details) {
					fields.siteCondition.field.from_json(result.bof_site_details);
					siteDetailsAutofillState("autofilled");
				} else {
					siteDetailsAutofillState("no_data");
				}

			}).catch(function(error) {
				siteDetailsAutofillState("error");
				throw error;
			});
		}

		survey.sharedSite.subscribe(function(site) {
			if(!site) {
				siteDetailsAutofillState("none");
			} else {
				if(!survey.existing()) {
					autofillSiteDetails(site);
				}
			}
		});

		return {
			fields,
			siteDetailsAutofillState,
			validations: ko.pureComputed(function() {
				// This is dynamic so that we can validate nested plant fields
				return Object.values(fields)
					.flatMap(f => f.field.withDescendants())
					.map(f => f.value);
				// return _.flatten(_.values(fields).map(f => f.field.withDescendants())).map(f => f.value);
			})
		};
	},
	updateSurveyJSON: function(json, survey) {
		var fields = Object.values(survey.programData().bof.fields);
		json.bof = {};

		for(let f of fields) {
			f.field.save(json.bof);
		}


		delete json.survey_type;
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.bof;

		for(let f of Object.values(fields)) {
			f.field.load(data);
		}
	},
	visibleSharedSiteLayers: () => ["bof"],
	siteBasedEntry: true,
	recordSiteID: true,
	defaultAreaLayer: (layers) => null,
	hasSiteDetailsTab: true,
	hideWaterLevel: true,
	hideSurveyType: true,
	surveyTypeOptions: [],
	forcePrivate: true,

};

export default program;
